import { createSelector } from "reselect"
import { chain, map, filter, find, get, includes, orderBy } from "lodash"
import pluralize from "pluralize"
import { ATTRIBUTION_COLORS, FACTORS_THRESHOLD } from "helpers/analyzeConstants"
import { getPerformanceOverallItemsWithResidual } from "helpers/residualAttribution"
import { computeAvailableSelectViewFactors, getFactorCategory, getFactorLabel, getIsAggregatedFactor, makeNonZeroKeysSelectorFromData } from "helpers/factors"
import { getDisabled, getFactorCategoryId as getSelectedFactorCategoryId } from "."
import { getCurrentPerformance, getRawPerformanceHistory } from "./performance"

export const getCategories = (_, props) => props.categories
export const getFavoriteFactors = (_, props) => props.favoriteFactors
const getSelectedViews = (_, props) => props.selectedViews
const getFactorsMetadata = (_, props) => props.factorsMetadata
const getFactorsClassification = (_, props) => props.factorsClassification

export const getFactorCategoryId = createSelector(
  [getSelectedFactorCategoryId, getCategories],
  (factorCategoryId, categories) => {
    if (factorCategoryId === "total" || factorCategoryId === "factors") {
      return factorCategoryId
    } else if (find(categories, cat => cat.id === factorCategoryId)) {
      return factorCategoryId
    }

    return "total"
  },
)

export const getComputedFactors = createSelector(
  [getFactorsMetadata, getCurrentPerformance, getDisabled, getFavoriteFactors],
  (factorsMetadata, lastDatum, disabled, favoriteFactors) => chain(lastDatum)
    .get("factors")
    .map((performance, id) => ({
      id,
      lastValue: performance,
      label: getFactorLabel(factorsMetadata, id),
      category: getFactorCategory(factorsMetadata, id),
      favorite: favoriteFactors[id],
      field: `factors.${id}`,
      disabled: includes(disabled, id),
      type: "factors",
      isAggregated: getIsAggregatedFactor(factorsMetadata, id),
    }))
    .value(),
)

const getNonZerosKeys = makeNonZeroKeysSelectorFromData({
  getter: (value) => get(value, "attribution.factors"),
  isAboveZeroTreshold: (value) => Math.abs(value) >= FACTORS_THRESHOLD,
  dataSelector: getRawPerformanceHistory,
  favoriteSelector: getFavoriteFactors,
  factorValueField: "return.cumulativePercent",
})

export const getAvailableSelectViewFactors = createSelector(
  [getComputedFactors, getFactorsClassification, getSelectedViews, getNonZerosKeys],
  computeAvailableSelectViewFactors,
)

export const getOverallItems = createSelector(
  [getCategories, getDisabled, getPerformanceOverallItemsWithResidual],
  (categories, disabled, filteredOverallItems) => {
    return [
      ...map(filteredOverallItems, item => ({ ...item, disabled: includes(disabled, item.id) })),
      {
        id: "factor_total",
        color: ATTRIBUTION_COLORS.FACTOR,
        label: "All Factors",
        categoryLabel: "Factors",
        field: "factorsTotal",
        disabled: includes(disabled, "factor_total"),
        type: "category",
      },
      ...chain(categories)
        .filter(category => !category.isSelectedView)
        .map(category => ({
          id: category.id,
          color: category.color,
          label: category.label,
          field: `categories.${category.id}`,
          disabled: includes(disabled, category.id),
          type: "category",
        })),
      ...map(categories, category => ({
        id: `${category.id}_total`,
        color: category.color,
        categoryLabel: category.isSelectedView ? category.label : pluralize(category.label),
        category: category.id,
        label: category.isSelectedView ? category.label : `All ${category.label} Factors`,
        field: `categories.${category.id}`,
        disabled: includes(disabled, `${category.id}_total`),
      })),
    ]
  },
)

export const getAllItems = createSelector(
  [getComputedFactors, getOverallItems],
  (factors, overallItems) => ([
    ...overallItems,
    ...factors,
  ]),
)

export const getAllCategoryItems = createSelector(
  [getComputedFactors, getOverallItems, getFactorCategoryId],
  (factors, overallItems, factorCategoryId = "total") => {
    switch (factorCategoryId) {
      case "total":
        return filter(overallItems, { type: "overall" })
      case "factors":
        return filter(overallItems, { type: "category" })
      default: {
        const overall = filter(overallItems, { category: factorCategoryId })
        return [
          ...overall,
          ...chain(factors)
            .filter({ category: factorCategoryId })
            .orderBy([item => Math.abs(item.lastValue)], ["desc"])
            .value(),
        ]
      }
    }
  },
)

export const getOrderedCategoryItems = createSelector(
  [getAllCategoryItems, getFactorCategoryId],
  (items, factorCategoryId = "total") => {
    if (factorCategoryId !== "total" && factorCategoryId !== "factors") {
      const overall = items[0]
      const factors = items.slice(1)
      return [
        overall,
        ...orderBy(factors, [item => item.favorite, item => Math.abs(item.lastValue)], ["asc", "desc"]),
      ]
    }

    return items
  },
)
